//Import external libraries
import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

//Import internal resources and services
import { getUser } from "../../utils/services/userService";
import { setSnackbar } from "../../actions/snackbarActions";
import { authenticate, isEmailNotConfirmedError } from "../../utils/Auth";
import { logIn } from "../../actions/authActions";
import { setUser } from "../../actions/userActions";
import { sendRecoveryEmailForgot } from "../../utils/Auth";
import { clearAuthStorage } from "../../utils/tokenUtils";
import {
  getPasswordValidationErrors,
  isPasswordFormatValid,
} from "../../utils/passwordValidation";

//Import MUI components
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Modal from "@mui/material/Modal";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTheme } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Popover from "@mui/material/Popover";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

function LoginModal(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [email1, setEmail1] = useState("");
  const [password, setPassword] = useState("");
  const [open, setOpen] = useState(false);
  const [emailErr, setEmailErr] = useState(false);
  const [pwdError, setPwdError] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const isRecoveryEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email1.trim());
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit =
    isEmailValid && isPasswordFormatValid(password) && !loading;

  const loginLinkSx = {
    color: theme.palette.secondary.main,
    textDecoration: "none",
    textUnderlineOffset: "3px",
    textDecorationThickness: "1.5px",
    cursor: "pointer",
    fontWeight: 400,
    fontSize: { xs: "14px", sm: "15px" },
    transition: "color 0.2s ease, opacity 0.2s ease",
    "&:hover": {
      color: theme.palette.secondary.dark,
      opacity: 0.85,
      textDecoration: "underline",
      fontWeight: 600,

    },
  };

  // Login form always uses language selected in the app (localStorage)
  useEffect(() => {
    if (!props.modalopen) return;

    const stored = (localStorage.getItem("i18nextLng") || "en")
      .split("-")[0]
      .toLowerCase();
    const supported = ["en", "sr", "de", "it", "ru"];
    const lng = supported.includes(stored) ? stored : "en";

    if (i18n.language?.split("-")[0] !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [props.modalopen, i18n]);

  // After registration / password reset / recovery email: empty fields, then snackbar
  useEffect(() => {
    const fromRegistration = Boolean(location.state?.registrationSuccess);
    const fromPasswordReset = Boolean(location.state?.passwordResetSuccess);
    const fromRecoveryEmail = Boolean(location.state?.recoveryEmailSent);

    if (!fromRegistration && !fromPasswordReset && !fromRecoveryEmail) return;

    setEmail("");
    setPassword("");
    setEmail1("");
    setEmailErr(false);
    setPwdError(false);
    setPasswordErrors([]);

    const message = fromRegistration
      ? t("snackbar.registerSuccess")
      : fromPasswordReset
        ? t("snackbar.passwordChangedSuccess")
        : t("snackbar.recoveryMailSuccess");

    const snackbarTimer = setTimeout(() => {
      dispatch(setSnackbar(true, "success", message));
      navigate(location.pathname, { replace: true, state: {} });
    }, 300);

    return () => clearTimeout(snackbarTimer);
  }, [
    location.state?.registrationSuccess,
    location.state?.passwordResetSuccess,
    location.state?.recoveryEmailSent,
    location.pathname,
    dispatch,
    navigate,
    t,
  ]);

  const handleCloseBackdrop = () => {
    setOpen(false);
  };
  const handleToggleBackdrop = () => {
    setOpen(!open);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const validateEmailValue = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setEmailErr(false);
      return false;
    }
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    setEmailErr(!valid);
    return valid;
  };

  const validatePasswordValue = (value) => {
    if (!value) {
      setPwdError(false);
      setPasswordErrors([]);
      return [];
    }
    const errors = getPasswordValidationErrors(value, t);
    setPwdError(errors.length > 0);
    setPasswordErrors(errors);
    return errors;
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Clear previous error while typing; validate only on blur
    if (emailErr) setEmailErr(false);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePasswordValue(value);
  };

  const sendRecoveryEmail = () => {
    sendRecoveryEmailForgot(email1)
      .then((res) => {
        const status = Number(res?.statusCode ?? res?.status);
        if (status >= 200 && status < 300) {
          setEmail1("");
          setOpen(false);
          dispatch(
            setSnackbar(true, "success", t("snackbar.recoveryMailSuccess")),
          );
          return;
        }
        dispatch(
          setSnackbar(
            true,
            "error",
            res?.message || t("snackbar.recoveryMailError"),
          ),
        );
      })
      .catch((err) =>
        dispatch(
          setSnackbar(
            true,
            "error",
            err?.response?.data?.message ||
              err?.message ||
              t("snackbar.recoveryMailError"),
          ),
        ),
      );
  };

  const handleLogIn = (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    authenticate(email, password)
      .then((res) => {
        const accessToken = res?.data?.token ?? (typeof res?.data === "string" ? res.data : null);

        if (res.statusCode === 200 && accessToken) {
          dispatch(logIn(accessToken));
          if (res.data?.refreshToken) {
            localStorage.setItem("refreshToken", res.data.refreshToken);
          }

          getUser(accessToken).then((results) => {
            dispatch(setUser(results.data.firstName));
          });

          setLoading(false);
          navigate("/");
          return;
        }

        clearAuthStorage();

        if (isEmailNotConfirmedError(res)) {
          dispatch(
            setSnackbar(
              true,
              "info",
              res?.message || t("snackbar.emailNotConfirmed"),
            ),
          );
        } else {
          dispatch(
            setSnackbar(
              true,
              "error",
              res?.message || t("snackbar.loginError"),
            ),
          );
        }
        notValid();
        setLoading(false);
      })
      .catch((err) => {
        clearAuthStorage();
        dispatch(
          setSnackbar(
            true,
            "error",
            err?.response?.data?.message ||
              err?.message ||
              t("snackbar.loginError"),
          ),
        );
        setLoading(false);
      });
  };

  const [values, setValues] = useState({
    amount: "",
    password: "",
    weight: "",
    weightRange: "",
    showPassword: false,
  });

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const notValid = () => {
    setEmail("");
    setPassword("");
    setEmailErr(false);
    setPwdError(false);
    setPasswordErrors([]);
  };

  //popover
  const [anchorEl1, setAnchorEl1] = React.useState(null);
  const [anchorEl2, setAnchorEl2] = React.useState(null);
  const handlePopoverOpen1 = (event) => {
    setAnchorEl1(event.currentTarget);
  };
  const handlePopoverOpen2 = (event) => {
    setAnchorEl2(event.currentTarget);
  };
  const handlePopoverClose1 = () => {
    setAnchorEl1(null);
  };
  const handlePopoverClose2 = () => {
    setAnchorEl2(null);
  };
  const openPopover1 = Boolean(anchorEl1);
  const openPopover2 = Boolean(anchorEl2);

  return (
    <Modal
      open={props.modalopen}
      onClose={props.closeModal}
      disableScrollLock={true}
      sx={{
        backdropFilter: "blur(8px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ThemeProvider theme={theme}>
        <Container
          component="main"
          sx={{
            width: { xs: 300, sm: 400, md: 400, lg: 700, xl: 800 },
            backgroundColor: theme.palette.primary.light,
            borderRadius: "5px",
            lineHeight: "0.5",
            boxShadow: "inset 0px 0px 0px 0px #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CssBaseline />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",

              padding: { xs: "40px 20px", sm: "25px 30px 35px 30px" },
              width: "100%",
            }}
          >
            <Avatar
              sx={{
                m: 1,
                color: theme.palette.secondary.main,
                background: theme.palette.primary.dark,
              }}
            >
              <LockOutlinedIcon />
            </Avatar>
            <Typography
              component="h1"
              variant="h4"
              sx={{ color: theme.palette.primary.main }}
            >
              {t("login.signin")}
            </Typography>

            <Box
              component="form"
              onSubmit={handleLogIn}
              noValidate
              sx={{ mt: 1, width: "100%" }}
            >
              <TextField
                required
                fullWidth
                id="email"
                label={t("login.email")}
                name="email"
                autoComplete="off"
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                }}
                value={email}
                onChange={handleEmailChange}
                onBlur={(e) => validateEmailValue(e.target.value)}
                helperText={emailErr ? t("login.emailinvalid") : ""}
                error={emailErr}
                sx={{
                  "& .MuiInputBase-input": {
                    "&:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 30px white inset",
                      WebkitTextFillColor: "#000",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                  },
                }}
              />
              <FormControl
                fullWidth
                required
                sx={{ mt: 2, mb: 2 }}
                error={pwdError}
              >
                <InputLabel htmlFor="outlined-adornment-password">
                  {t("login.password")}
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password"
                  margin="none"
                  name="password"
                  label={t("login.password")}
                  type={values.showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  inputProps={{
                    autoComplete: "new-password",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                  }}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={(e) => validatePasswordValue(e.target.value)}
                  error={pwdError}
                  sx={{
                    "& .MuiInputBase-input": {
                      "&:-webkit-autofill": {
                        WebkitBoxShadow: "0 0 0 30px white inset",
                        WebkitTextFillColor: "#000",
                        transition: "background-color 5000s ease-in-out 0s",
                      },
                    },
                  }}
                  endAdornment={
                    <InputAdornment
                      position="end"
                      sx={{ right: 20, position: "absolute" }}
                    >
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        tabIndex={-1}
                      >
                        {values.showPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {passwordErrors.map((errorText, index) => (
                  <FormHelperText error key={`login-pwd-err-${index}`}>
                    {errorText}
                  </FormHelperText>
                ))}
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!canSubmit}
                sx={{
                  mt: { xs: 0 },
                  mb: 2,
                  color: theme.palette.primary.light,
                  background: theme.palette.secondary.main,
                  fontSize: { xs: "18px", sm: "24px" },
                  fontWeight: "400",
                  transform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                    color: theme.palette.secondary.main,
                  },
                  "&.Mui-disabled": {
                    background: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                {t("login.signinbtn")}
              </Button>
              {/* {emailErr && <p>Your email is invalid</p>} */}
              {/* {pwdError && <p>Your password is invalid</p>} */}
              <Grid container sx={{ display: { xs: "block", sm: "flex" } }}>
                <Grid item xs>
                  {/* <Link to='/login/password-reset/send-email' variant="body2"> */}
                  <Link
                    className="link"
                    href="#"
                    variant="body1"
                    onClick={handleClickOpen}
                    sx={loginLinkSx}
                    aria-owns={openPopover1 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen1}
                    onMouseLeave={handlePopoverClose1}
                  >
                    {t("login.forgotpassword")}
                  </Link>
                  <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: "none" }}
                    open={openPopover1}
                    anchorEl={anchorEl1}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose1}
                    //disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>{t("login.fpinfo")}</Typography>
                  </Popover>
                  <Dialog open={open} onClose={handleClose}>
                    <DialogTitle>{t("login.dialogtitle")}</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        {t("login.dialogtext")}
                      </DialogContentText>
                      <TextField
                        autoFocus
                        margin="dense"
                        //id="name"
                        label={t("login.email")}
                        type="email"
                        fullWidth
                        variant="standard"
                        value={email1}
                        onChange={(e) => setEmail1(e.target.value)}
                        sx={{
                          textAlign: "center",
                          justifyItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={handleClose}
                        sx={{ color: theme.palette.secondary.main }}
                      >
                        {t("login.cancelbtn")}
                      </Button>
                      <Button
                        onClick={sendRecoveryEmail}
                        disabled={!isRecoveryEmailValid}
                        sx={{ color: theme.palette.secondary.main }}
                      >
                        {t("login.confirmbtn")}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Grid>
                <Grid item>
                  <Link
                    className="link"
                    href="/register"
                    variant="body1"
                    sx={loginLinkSx}
                    aria-owns={openPopover2 ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen2}
                    onMouseLeave={handlePopoverClose2}
                  >
                    {t("login.createaccount")}
                  </Link>
                  <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: "none" }}
                    open={openPopover2}
                    anchorEl={anchorEl2}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    onClose={handlePopoverClose2}
                    //disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>
                      {t("login.accountinfo")}
                    </Typography>
                  </Popover>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
        <Backdrop
          sx={{
            color: theme.palette.primary.light,
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={loading}
          onClick={handleCloseBackdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </ThemeProvider>
    </Modal>
  );
}

export default LoginModal;
